import { useCallback, useMemo, useState } from "react";
import request from "../../../utils/request";
import {
  EMAIL_REGEX,
  IMPORT_FIELDS,
  PHONE_REGEX,
  URL_REGEX,
} from "../constants";
import { getApiErrorMessage } from "../workspace/utils";

const STEPS = ["upload", "columns", "mapping", "validation", "review"];

function detectColumnType(header, sampleValues) {
  const lower = (header || "").toLowerCase();
  const nonEmpty = sampleValues.filter(Boolean);
  const ratio = (regex) =>
    nonEmpty.length
      ? nonEmpty.filter((value) => regex.test(String(value).trim())).length / nonEmpty.length
      : 0;

  if (ratio(EMAIL_REGEX) >= 0.5 || lower.includes("email")) return "email";
  if (ratio(URL_REGEX) >= 0.5 || lower.includes("website") || lower.includes("url") || lower.includes("domain")) return "url";
  if (ratio(PHONE_REGEX) >= 0.5 || lower.includes("phone") || lower.includes("mobile")) return "phone";
  return "text";
}

function validateCell(value, type) {
  if (value === undefined || value === null || value === "") return { ok: true };
  const trimmed = String(value).trim();
  if (type === "email" && !EMAIL_REGEX.test(trimmed)) return { ok: false, reason: "Invalid email" };
  if (type === "url" && !URL_REGEX.test(trimmed)) return { ok: false, reason: "Suspicious URL" };
  if (type === "phone" && !PHONE_REGEX.test(trimmed)) return { ok: false, reason: "Suspicious phone" };
  return { ok: true };
}

const INITIAL_STATE = {
  step: "upload",
  file: null,
  loading: false,
  committing: false,
  error: "",
  preview: null,
  mapping: {},
  suggestedMapping: {},
  selectedHeaders: [],
  headerSearch: "",
  headerTypes: {},
  excludeErrors: true,
  result: null,
};

export default function useCsvImport({ activeCampaignId, lastMapping }) {
  const [state, setState] = useState(INITIAL_STATE);

  const update = useCallback((patch) => {
    setState((current) => ({
      ...current,
      ...(typeof patch === "function" ? patch(current) : patch),
    }));
  }, []);

  const reset = useCallback(() => setState(INITIAL_STATE), []);

  const goToStep = useCallback((step) => {
    if (!STEPS.includes(step)) return;
    update({ step });
  }, [update]);

  const previewFile = useCallback(
    async (file) => {
      if (!file || !activeCampaignId) return;
      update({ file, loading: true, error: "", preview: null, step: "columns" });

      const formData = new FormData();
      formData.append("file", file);

      try {
        const { data } = await request.post(
          `/campaigns/${activeCampaignId}/import/preview`,
          formData,
          {
            withCredentials: true,
            headers: { "Content-Type": "multipart/form-data" },
          }
        );

        const headers = data.headers || [];
        const sampleRows = data.sampleRows || [];
        const headerTypes = {};
        for (const header of headers) {
          const samples = sampleRows.map((row) => row?.[header]).slice(0, 20);
          headerTypes[header] = detectColumnType(header, samples);
        }

        const mergedMapping = { ...(lastMapping || {}), ...(data.suggestedMapping || {}) };

        update({
          preview: data,
          loading: false,
          suggestedMapping: mergedMapping,
          mapping: mergedMapping,
          selectedHeaders: headers,
          headerTypes,
        });
      } catch (error) {
        console.error("Failed to preview CSV", error);
        update({
          loading: false,
          error: getApiErrorMessage(error, "Preview failed"),
          step: "upload",
        });
      }
    },
    [activeCampaignId, lastMapping, update]
  );

  const toggleHeader = useCallback(
    (header) => {
      update((current) => {
        const selected = new Set(current.selectedHeaders);
        const nextMapping = { ...current.mapping };
        if (selected.has(header)) {
          selected.delete(header);
          for (const key of Object.keys(nextMapping)) {
            if (nextMapping[key] === header) delete nextMapping[key];
          }
        } else {
          selected.add(header);
        }
        const ordered = (current.preview?.headers || []).filter((h) => selected.has(h));
        return { selectedHeaders: ordered, mapping: nextMapping };
      });
    },
    [update]
  );

  const setHeaderSearch = useCallback(
    (value) => update({ headerSearch: value }),
    [update]
  );

  const selectAllHeaders = useCallback(() => {
    update((current) => ({ selectedHeaders: current.preview?.headers || [] }));
  }, [update]);

  const clearHeaders = useCallback(() => {
    update({ selectedHeaders: [], mapping: {} });
  }, [update]);

  const setMapping = useCallback(
    (field, header) => {
      update((current) => {
        const nextMapping = { ...current.mapping };
        if (!header) {
          delete nextMapping[field];
        } else {
          for (const key of Object.keys(nextMapping)) {
            if (key !== field && nextMapping[key] === header) {
              delete nextMapping[key];
            }
          }
          nextMapping[field] = header;
        }
        const selected = new Set(current.selectedHeaders);
        if (header) selected.add(header);
        const ordered = (current.preview?.headers || []).filter((h) => selected.has(h));
        return { mapping: nextMapping, selectedHeaders: ordered };
      });
    },
    [update]
  );

  const resetMapping = useCallback(() => {
    update((current) => ({
      mapping: { ...(current.suggestedMapping || {}) },
    }));
  }, [update]);

  const setExcludeErrors = useCallback(
    (value) => update({ excludeErrors: value }),
    [update]
  );

  // Client-side validation pass over the preview rows using the mapped fields.
  // Produces per-row issues and summary counts.
  const validation = useMemo(() => {
    if (!state.preview) return null;
    const rows = state.preview.sampleRows || [];
    const mapping = state.mapping || {};
    const requiredFields = IMPORT_FIELDS.filter((f) => f.required).map((f) => f.key);

    const seenEmails = new Map();
    const annotated = rows.map((row, index) => {
      const issues = [];
      let hasEmail = false;

      for (const field of IMPORT_FIELDS) {
        const header = mapping[field.key];
        if (!header) {
          if (field.required) {
            issues.push({ level: "error", message: `${field.label} not mapped` });
          }
          continue;
        }
        const value = row?.[header];
        if (field.required && !value) {
          issues.push({ level: "error", message: `Missing ${field.label.toLowerCase()}` });
        }
        if (field.key === "email" && value) {
          hasEmail = true;
          if (!EMAIL_REGEX.test(String(value).trim())) {
            issues.push({ level: "error", message: "Invalid email format" });
          } else {
            const normalized = String(value).trim().toLowerCase();
            if (seenEmails.has(normalized)) {
              issues.push({
                level: "warning",
                message: `Duplicate of row ${seenEmails.get(normalized) + 1}`,
              });
            } else {
              seenEmails.set(normalized, index);
            }
          }
        }
        const type = state.headerTypes?.[header];
        if (type && type !== "text" && field.key !== "email") {
          const outcome = validateCell(value, type);
          if (!outcome.ok) {
            issues.push({ level: "warning", message: outcome.reason });
          }
        }
      }

      if (!hasEmail && requiredFields.includes("email")) {
        if (!issues.find((i) => i.message?.startsWith("Missing email"))) {
          issues.push({ level: "error", message: "Missing email" });
        }
      }

      return { index, row, issues, hasError: issues.some((i) => i.level === "error") };
    });

    const counts = {
      total: state.preview.counts?.rows || rows.length,
      sampled: rows.length,
      errors: annotated.filter((r) => r.hasError).length,
      warnings: annotated.filter((r) => !r.hasError && r.issues.length).length,
      clean: annotated.filter((r) => !r.issues.length).length,
      matched: state.preview.counts?.matched || 0,
      new: state.preview.counts?.new || 0,
    };

    return { rows: annotated, counts };
  }, [state.headerTypes, state.mapping, state.preview]);

  const canAdvance = useCallback(
    (step) => {
      if (step === "columns") {
        return state.selectedHeaders.length > 0;
      }
      if (step === "mapping") {
        for (const field of IMPORT_FIELDS) {
          if (field.required && !state.mapping[field.key]) return false;
        }
        return true;
      }
      if (step === "validation") return validation != null;
      return true;
    },
    [state.mapping, state.selectedHeaders.length, validation]
  );

  const commit = useCallback(
    async (onSuccess) => {
      if (!activeCampaignId || !state.preview?.sessionId) return;
      update({ committing: true, error: "" });

      try {
        const payload = {
          sessionId: state.preview.sessionId,
          mapping: state.mapping,
        };
        if (state.excludeErrors && validation?.rows?.length) {
          const skipRowIndexes = validation.rows
            .filter((row) => row.hasError)
            .map((row) => row.index);
          if (skipRowIndexes.length) {
            // If the server supports it, it can use `skipRowIndexes`. It's safe to ignore.
            payload.skipRowIndexes = skipRowIndexes;
          }
        }
        const { data } = await request.post(
          `/campaigns/${activeCampaignId}/import/commit`,
          payload,
          { withCredentials: true }
        );
        update({ committing: false, result: data, step: "upload" });
        reset();
        onSuccess?.(data);
      } catch (error) {
        console.error("Failed to commit CSV import", error);
        update({
          committing: false,
          error: getApiErrorMessage(error, "Import failed"),
        });
      }
    },
    [activeCampaignId, reset, state.excludeErrors, state.mapping, state.preview, update, validation]
  );

  const mappedFieldCount = useMemo(
    () => Object.values(state.mapping || {}).filter(Boolean).length,
    [state.mapping]
  );

  return {
    state,
    validation,
    mappedFieldCount,
    goToStep,
    previewFile,
    toggleHeader,
    selectAllHeaders,
    clearHeaders,
    setMapping,
    resetMapping,
    setHeaderSearch,
    setExcludeErrors,
    canAdvance,
    commit,
    reset,
  };
}

export { STEPS };
